import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import type {
  ReclaimAccessDefaults,
  ReclaimAccessSource,
} from "@redotech/redo-model/disputes/reclaim-access-settings";
import {
  DEFAULT_RECLAIM_ACCESS_DEFAULTS,
  hydrateMerchantReclaimAccessDefaults,
} from "@redotech/redo-model/disputes/reclaim-access-settings";
import { getReclaimCommercialTermsCopy } from "@redotech/redo-model/disputes/reclaim-onboarding-v2-mirror";
import { useContext, useMemo, type ContextType } from "react";

type ReclaimRpcClient = NonNullable<
  ContextType<typeof RedoMerchantRpcClientContext>
>;

let cachedReclaimAccessDefaults: ReclaimAccessDefaults | null = null;
let pendingReclaimAccessDefaults: Promise<ReclaimAccessDefaults> | null = null;

export function useReclaimAccessDefaults({
  enabled = true,
  source,
}: { enabled?: boolean; source?: ReclaimAccessSource } = {}): {
  accessDefaults: ReclaimAccessDefaults;
  error: unknown;
  pending: boolean;
  termsCopy: ReturnType<typeof getReclaimCommercialTermsCopy>;
} {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const team = useContext(TeamContext);
  const load = useLoad(
    async () =>
      enabled
        ? loadReclaimAccessDefaults({ rpcClient })
        : DEFAULT_RECLAIM_ACCESS_DEFAULTS,
    [enabled, rpcClient],
  );
  // Defaults are not sensitive and only affect merchant copy/access affordances.
  // If the read fails, keep the UI usable with the static product defaults.
  const accessDefaults =
    load.value ??
    cachedReclaimAccessDefaults ??
    DEFAULT_RECLAIM_ACCESS_DEFAULTS;
  const termsCopy = useMemo(
    () =>
      getReclaimCommercialTermsCopy({
        accessDefaults,
        disputes: team?.settings.disputes,
        source,
      }),
    [accessDefaults, source, team?.settings.disputes],
  );

  return {
    accessDefaults,
    error: load.error,
    pending:
      enabled && !load.error && !load.value && !cachedReclaimAccessDefaults,
    termsCopy,
  };
}

export function clearReclaimAccessDefaultsCacheForTest(): void {
  cachedReclaimAccessDefaults = null;
  pendingReclaimAccessDefaults = null;
}

async function loadReclaimAccessDefaults({
  rpcClient,
}: {
  rpcClient: ReclaimRpcClient;
}): Promise<ReclaimAccessDefaults> {
  if (cachedReclaimAccessDefaults) {
    return cachedReclaimAccessDefaults;
  }
  if (!pendingReclaimAccessDefaults) {
    pendingReclaimAccessDefaults = rpcClient
      .getReclaimAccessDefaults({})
      .then((result) => {
        const hydrated = hydrateMerchantReclaimAccessDefaults(result.defaults);
        cachedReclaimAccessDefaults = hydrated;
        return hydrated;
      })
      .catch(() => DEFAULT_RECLAIM_ACCESS_DEFAULTS)
      .finally(() => {
        pendingReclaimAccessDefaults = null;
      });
  }
  return pendingReclaimAccessDefaults;
}
